/**
 * Hook for fetching and managing team data.
 * Combines team members and pending invitations in a single hook.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { getTeam, getPendingInvitations } from '../services/teamService';
import type { TeamMember, TeamUsageStats, TeamInvitation } from '../types/company';

interface UseTeamReturn {
  members: TeamMember[];
  invitations: TeamInvitation[];
  usage: TeamUsageStats | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useTeam(): UseTeamReturn {
  const { session } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [usage, setUsage] = useState<TeamUsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!session?.token) {
      setIsLoading(false);
      setError('Not authenticated');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch team members and invitations in parallel
      const [teamData, inviteData] = await Promise.all([
        getTeam(session.token),
        getPendingInvitations(session.token),
      ]);

      setMembers(teamData.members);
      setUsage(teamData.usage);
      setInvitations(inviteData.invitations);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load team';
      setError(message);
      console.error('Failed to fetch team data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session?.token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    members,
    invitations,
    usage,
    isLoading,
    error,
    refetch: fetchData,
  };
}
